import React, { useState, useEffect } from 'react';

const NewsletterSubscription = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState('');          // user-facing feedback
  const [deletedFormUrl, setDeletedFormUrl] = useState(''); // hosted form URL for deleted_email

  // Helper: parse JSON or fallback to text (handles CRA proxy errors)
  const parseResponse = async (res) => {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) return res.json();
    const text = await res.text();
    if (text.startsWith('Proxy error')) {
      throw new Error('Não foi possível conectar ao servidor. Tente novamente.');
    }
    throw new Error(text || 'Erro inesperado do servidor.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setDeletedFormUrl('');
    if (!name || !email) {
      setMessage('Por favor, preencha todos os campos.');
      return;
    }

    setSubmitting(true);
    try {
      // Configurable endpoint for dev/prod. Defaults to PHP route under /api
      const endpoint = process.env.REACT_APP_NEWSLETTER_ENDPOINT || '/api/newsletter-subscribe.php';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, company }),
      });

      const data = await parseResponse(res);

      // Edge case: permanently deleted contact → must re-subscribe via hosted form
      if (!res.ok && res.status === 409 && data?.error === 'deleted_email') {
        const msg =
          data?.message ||
          'Este e-mail foi removido permanentemente. Reinscreva-se pelo formulário oficial.';
        setMessage(msg);

        if (data?.formUrl) {
          setDeletedFormUrl(data.formUrl);
          // try to open a new tab automatically; CTA below remains as fallback
          try { window.open(data.formUrl, '_blank', 'noopener'); } catch {}
        }
        return;
      }

      if (!res.ok) {
        throw new Error(data?.error || 'Falha ao enviar. Tente novamente.');
      }

      // Success (subscribed or pending)
      const successMsg =
        data?.status === 'subscribed'
          ? 'Inscrição confirmada.'
          : (data?.message || 'Inscrição recebida. Confira seu e-mail para confirmar.');
      setMessage(successMsg);
      setName('');
      setEmail('');
      setCompany('');
    } catch (err) {
      setMessage(err.message || 'Ocorreu um erro. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Optional: auto-hide generic messages after a while (keep visible if showing CTA)
  useEffect(() => {
    if (!message || deletedFormUrl) return;
    const t = setTimeout(() => setMessage(''), 8000);
    return () => clearTimeout(t);
  }, [message, deletedFormUrl]);

  return (
    <section
      aria-labelledby="newsletter-title"
      className="
        relative w-full
        bg-[#F6F6F6] bg-no-repeat bg-bottom
        bg-cover md:bg-[length:100%_auto]
        px-4 sm:px-6 lg:px-8
        pt-16 sm:pt-20
        pb-28 md:pb-36
      "
      style={{ backgroundImage: "url('/images/background/hexagon-home-min.jpg')" }}
    >
      <div className="max-w-4xl mx-auto text-center">
        <h2 id="newsletter-title" className="text-3xl font-bold text-gray-900 dark:text-gray-900 sm:text-4xl">
          Assine nossa newsletter mensal
        </h2>
        <p className="mt-4 text-center mx-auto max-w-xl text-base text-gray-700 dark:text-gray-700">
          Preencha os campos abaixo para receber novidades, dicas de cuidado e atualizações
          da Gtechniq diretamente no seu e-mail.
        </p>

        {/* Honeypot invisível */}
        <div className="absolute -left-[9999px] top-0 opacity-0 pointer-events-none" aria-hidden="true">
          <label htmlFor="company">Empresa</label>
          <input
            id="company"
            name="company"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>

        <form onSubmit={handleSubmit} className="mt-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="name" className="sr-only">Nome</label>
              <input
                id="name"
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                autoComplete="name"
                required
                className="w-full h-16 px-4 py-2 border border-gray-300 focus:ring-[#FF0000] focus:border-[#FF0000]"
              />
            </div>

            <div className="flex-1">
              <label htmlFor="email" className="sr-only">E-mail</label>
              <input
                id="email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu e-mail"
                autoComplete="email"
                required
                className="w-full h-16 px-4 py-2 border border-gray-300 focus:ring-[#FF0000] focus:border-[#FF0000]"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-4 w-full h-16 px-4 py-2 bg-[#FF0000] text-white text-lg font-bold tracking-wide border border-transparent transition-all duration-500 hover:bg-white hover:text-[#FF0000] hover:border-[#FF0000] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Enviando…' : 'Assinar'}
          </button>
        </form>

        {/* Feedback + hosted form CTA (only for deleted_email case) */}
        {message && (
          <div className="mt-4 text-sm text-center text-gray-800 dark:text-gray-800" aria-live="polite">
            {message}
            {deletedFormUrl ? (
              <div className="mt-3">
                <a
                  href={deletedFormUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 text-sm font-semibold text-white bg-[#FF0000] hover:bg-white hover:text-[#FF0000] border border-[#FF0000] transition"
                >
                  Reinscrever pelo formulário oficial
                </a>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsletterSubscription;
